import { Box } from '@chakra-ui/react';
import { RichText } from '@payloadcms/richtext-lexical/react';
import type { LexicalContent } from '@fluenceur/types';

export interface RichTextContentProps {
  data: LexicalContent;
}

/**
 * Reusable component for rendering Payload CMS Lexical rich text
 * with proper hierarchical heading styles and formatting
 */
export function RichTextContent({ data }: RichTextContentProps) {
  return (
    <Box
      css={{
        fontSize: '1rem',
        lineHeight: '1.6',
        '& h1': {
          fontSize: '1.875rem',
          fontWeight: '700',
          marginTop: '1.5rem',
          marginBottom: '1rem',
        },
        '& h2': {
          fontSize: '1.5rem',
          fontWeight: '600',
          marginTop: '1.25rem',
          marginBottom: '0.75rem',
        },
        '& h3': {
          fontSize: '1.25rem',
          fontWeight: '600',
          marginTop: '1rem',
          marginBottom: '0.5rem',
        },
        '& h4': {
          fontSize: '1rem',
          fontWeight: '500',
          marginTop: '0.75rem',
          marginBottom: '0.5rem',
        },
        '& h5': {
          fontSize: '0.875rem',
          fontWeight: '500',
          marginTop: '0.5rem',
          marginBottom: '0.25rem',
        },
        '& h6': {
          fontSize: '0.75rem',
          fontWeight: '500',
          marginTop: '0.5rem',
          marginBottom: '0.25rem',
        },
        '& p': {
          marginBottom: '0.75rem',
        },
        '& ul': {
          listStyleType: 'disc',
          marginLeft: '1.5rem',
          marginBottom: '0.75rem',
          paddingLeft: '0.5rem',
        },
        '& ol': {
          listStyleType: 'decimal',
          marginLeft: '1.5rem',
          marginBottom: '0.75rem',
          paddingLeft: '0.5rem',
        },
        '& li': {
          marginBottom: '0.25rem',
          display: 'list-item',
        },
        '& a': {
          color: '#3182ce',
          textDecoration: 'underline',
          '&:hover': {
            color: '#2c5282',
          },
        },
        '& strong, & b': {
          fontWeight: '600',
        },
        '& em, & i': {
          fontStyle: 'italic',
        },
        '& code': {
          backgroundColor: '#f7fafc',
          padding: '0.25rem',
          borderRadius: '0.25rem',
          fontSize: '0.875rem',
          fontFamily: 'monospace',
        },
        '& pre': {
          backgroundColor: '#f7fafc',
          padding: '0.75rem',
          borderRadius: '0.375rem',
          overflow: 'auto',
          marginBottom: '0.75rem',
        },
        '& blockquote': {
          borderLeft: '4px solid #e2e8f0',
          paddingLeft: '1rem',
          marginLeft: '0',
          marginBottom: '0.75rem',
          fontStyle: 'italic',
          color: '#718096',
        },
        '& hr': {
          marginTop: '1rem',
          marginBottom: '1rem',
          borderColor: '#e2e8f0',
        },
        '& table': {
          width: '100%',
          marginBottom: '0.75rem',
          borderCollapse: 'collapse',
        },
        '& th': {
          fontWeight: '600',
          textAlign: 'left',
          padding: '0.5rem',
          borderBottom: '2px solid #cbd5e0',
        },
        '& td': {
          padding: '0.5rem',
          borderBottom: '1px solid #e2e8f0',
        },
      }}
    >
      <RichText data={data} />
    </Box>
  );
}
